import type { FC } from 'react';
import React, { useContext, Fragment } from 'react';
import type { IntlFormatters } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl';

import Heading from '@atlaskit/heading';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import type { RoutesContextType } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { SpacePermissionsSharedHeader } from '@confluence/space-permissions/entry-points/SpacePermissionsSettingsTab.tsx';
import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode.ts';
import { expValEquals } from '@confluence/feature-experiments';

import {
	useSelectedNavigationWebItems,
	isSpacePermissionsRoute,
	isHubPermissionsRoute,
} from '../helpers/webItem.ts';
import { useSpaceSettingsWebItemSections } from '../web-item-sections/useSpaceSettingsWebItemSections.tsx';
import type { SpaceSettingsWebItemSectionsFragment_webItemSections } from '../web-item-sections/__types__/SpaceSettingsWebItemSectionsFragment.ts';

const LEGACY_PDF_EXPORT_COMPLETEKEY = 'com.atlassian.confluence.extra.flyingpdf:configpdf';

const i18n = defineMessages({
	spaceDetailsPageTitle: {
		id: 'space-settings.space-settings-navigation.space-details-page.title',
		defaultMessage: 'Space details',
		description: 'Page title for the space details setting page',
	},
});

const NavContainerStyles = xcss({
	width: '100%',
	position: 'relative',
});

const TitleAndFilterBarStyles = xcss({
	marginBlockEnd: 'space.400',
});

type GetTitleFromWebItemPayload = string | null | undefined;

const getTitleFromWebItem = ({
	webItemSection,
	itemCompleteKey,
}: {
	webItemSection: SpaceSettingsWebItemSectionsFragment_webItemSections | null | undefined;
	itemCompleteKey: string | null | undefined;
}): GetTitleFromWebItemPayload => {
	// Skip title rendering for PDF export page if NCS is enabled or in rollout - it will add its own title
	if (
		itemCompleteKey === LEGACY_PDF_EXPORT_COMPLETEKEY &&
		expValEquals('confluence_pdf_ncs_enabled', 'isEnabled', true)
	) {
		return null;
	}
	const innerTitle = webItemSection?.items?.find(
		(item) => item?.completeKey === itemCompleteKey,
	)?.label;
	const outerTitle = webItemSection?.label;
	return innerTitle || outerTitle;
};

const getTitleFromPathname = ({
	locationPathname,
	formatMessage,
}: {
	locationPathname: string;
	formatMessage: IntlFormatters['formatMessage'];
}): string | undefined => {
	if (
		locationPathname.includes('viewspacesummary.action') ||
		locationPathname.includes('editspace.action') ||
		locationPathname.includes('editspacelabels.action')
	) {
		// This is a special case for the space details page, which doesn't have a web item section
		// now that it's been migrated to the new navigation. So we've hardcoded the title here until
		// we are able to uplift the page from the monolith, at which point it will no longer render
		// via this component.
		return formatMessage(i18n.spaceDetailsPageTitle);
	} else {
		return undefined;
	}
};

export type SpaceSettingsNavigationV2Props = {
	children?: (props: { withCompleteNav: boolean }) => React.ReactNode;
	spaceKey: string;
};

/**
 * Renders Confluence Space Settings V2 tab navigation, including Connect and Forge apps.
 * This navigation may be used both on legacy monolith SSR space settings pages (where
 * it is injected via React portal), and on fully React SPA space settings pages (currently
 * only the Forge app space settings page).
 * This component handles only rendering the navigation tabs, not page contents.
 */
export const SpaceSettingsNavigationV2: FC<SpaceSettingsNavigationV2Props> = ({
	children,
	spaceKey,
}) => {
	const { formatMessage } = useIntl();
	const { location, matchRoute } = useContext<RoutesContextType>(RoutesContext);
	const { isRbacEnabled } = useSpaceRoleMode();
	const { loading, error, webItemSections } = useSpaceSettingsWebItemSections({
		spaceAlias: spaceKey,
	});

	const { innerNavigationWebItem, outerNavigationWebItem } = useSelectedNavigationWebItems({
		matchRoute,
		location,
		webItemSections,
		skip: loading || !!error,
	});

	if (loading) {
		return null;
	} else if (error) {
		// Something went wrong; report it
		return <ErrorDisplay error={error} />;
	}

	const isSpaceOrHubPermsPage =
		isSpacePermissionsRoute(matchRoute, location) || isHubPermissionsRoute(matchRoute, location);
	const matchingWebItemSection = webItemSections?.find(
		(section) => section?.id === outerNavigationWebItem?.id,
	);
	const titleFromWebItem = getTitleFromWebItem({
		webItemSection: matchingWebItemSection,
		itemCompleteKey: innerNavigationWebItem?.completeKey,
	});
	const titleFromPathname = getTitleFromPathname({
		locationPathname: location?.pathname || '',
		formatMessage,
	});

	return (
		<Box xcss={NavContainerStyles}>
			<Fragment>
				{(titleFromWebItem || titleFromPathname) && (
					<Box xcss={TitleAndFilterBarStyles}>
						<Heading size="large" as="h1">
							{titleFromWebItem || titleFromPathname}
						</Heading>
					</Box>
				)}
				{isSpaceOrHubPermsPage && !isRbacEnabled && (
					<SpacePermissionsSharedHeader spaceKey={spaceKey} />
				)}
			</Fragment>
			{typeof children === 'function' ? children({ withCompleteNav: true }) : children}
		</Box>
	);
};
